import { useEffect, useState } from "react";
import { Calculator, Send, Loader2, Banknote, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, inr, MONTHS, DEPT, formatErr, downloadFile } from "@/lib/api";

const STATUS_CLS = { draft: "bg-slate-100 text-slate-700", pending_approval: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-800", disbursed: "bg-sky-100 text-sky-800" };
const STATUS_TXT = { draft: "Draft", pending_approval: "Pending Approval", approved: "Approved", disbursed: "Disbursed" };

export default function Payroll() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/payroll", { params: { month, year } }).then((r) => setRows(r.data));
  useEffect(() => {
    load();
  }, [month, year]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (kind, url, msg) => {
    setBusy(kind);
    try {
      const { data } = await api.post(url, { month, year });
      if (Array.isArray(data)) setRows(data);
      else load();
      toast.success(msg);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy("");
    }
  };

  const status = rows[0]?.approval_status;
  const total = rows.reduce((s, r) => s + r.final_payout, 0);
  const locked = ["approved", "disbursed"].includes(status);

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-4xl font-extrabold text-slate-900 tracking-tight">Payroll Processing</h1>
          <p className="text-sm text-slate-500 mt-1 font-mono-num">Net = (Base ÷ Days in month) × Days worked + Daily allowance × Days worked · Half day = 0.5</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger className="w-36 bg-white" data-testid="payroll-month-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-24 bg-white" data-testid="payroll-year-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{[year - 1, year, year + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 bg-white border rounded-2xl p-4">
        <Button onClick={() => act("calc", "/payroll/calculate", "Payroll calculated from attendance")} disabled={!!busy || locked} className="bg-slate-900 hover:bg-slate-800" data-testid="payroll-calculate-button">
          {busy === "calc" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Calculator className="h-4 w-4 mr-2" />Calculate Monthly Payroll</>}
        </Button>
        <Button onClick={() => act("send", "/payroll/submit", "Sent to Director for approval")} disabled={!!busy || status !== "draft"} className="bg-sky-600 hover:bg-sky-700" data-testid="payroll-send-director-button">
          {busy === "send" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" />Send to Director for Approval</>}
        </Button>
        {status === "approved" && (
          <Button variant="outline" onClick={() => act("disb", "/payroll/disburse", "Marked as disbursed")} disabled={!!busy} data-testid="payroll-disburse-button"><Banknote className="h-4 w-4 mr-2" />Mark Disbursed</Button>
        )}
        <div className="ml-auto text-right">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Total Net Payable</div>
          <div className="font-heading text-2xl font-extrabold font-mono-num" data-testid="payroll-total">{inr(total)}</div>
        </div>
        {status && <span className={`px-3 py-1 rounded-full text-xs font-semibold ${STATUS_CLS[status]}`} data-testid="payroll-status-badge">{STATUS_TXT[status]}</span>}
      </div>
      <div className="bg-white border rounded-2xl overflow-x-auto" data-testid="payroll-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Staff</TableHead><TableHead>Department</TableHead><TableHead className="text-right">Base Salary</TableHead>
              <TableHead className="text-right">Working Days</TableHead><TableHead className="text-right">Attendance Deductions</TableHead>
              <TableHead className="text-right">Allowances</TableHead><TableHead className="text-right">Net Salary</TableHead><TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-10">Not calculated yet. Click "Calculate Monthly Payroll".</TableCell></TableRow>}
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`payroll-row-${r.user_id}`}>
                <TableCell className="font-semibold">{r.full_name}</TableCell>
                <TableCell className="text-slate-600">{DEPT[r.department]}</TableCell>
                <TableCell className="text-right font-mono-num">{inr(r.base_salary)}</TableCell>
                <TableCell className="text-right font-mono-num">{r.days_worked} / {r.days_in_month}</TableCell>
                <TableCell className="text-right font-mono-num text-rose-600">-{inr(r.leave_deductions)}</TableCell>
                <TableCell className="text-right font-mono-num text-emerald-700">+{inr(r.allowances)}</TableCell>
                <TableCell className="text-right font-mono-num font-bold">{inr(r.final_payout)}</TableCell>
                <TableCell>
                  {r.payslip_pdf_url && (
                    <Button size="icon" variant="ghost" onClick={() => downloadFile(`/payroll/${r.id}/payslip`, `Payslip_${r.full_name}_${year}_${month}.pdf`)} data-testid={`payroll-payslip-download-${r.user_id}`}>
                      <Download className="h-4 w-4" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
